/**
 * TransferOutSection component
 * Read-only display of daily transfer-out data on the End of Day Summary page.
 * Shows 4 summary cards (Finished Products, Rejected, Remaining, By-Products)
 * and 4 category tables with variance highlighting and yield factor comparison.
 * Reuses data patterns from DailyTransferOutView without action controls.
 */
import React from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import {
  DailyTransferOut,
  DailyTransferOutItem,
} from '../../../../services/dailyTransferOutService';
import {
  formatYield,
  getItemYieldVariance,
  getItemActualYF,
  isYieldVarianceWarning,
  getYieldVarianceColor,
  getActualYFColor,
  formatYieldVariancePct,
} from '../../../../hooks/useYieldFactor';
import {
  Truck,
  Package,
  AlertTriangle,
  Archive,
  Boxes,
} from 'lucide-react';

interface TransferOutSectionProps {
  data: DailyTransferOut | undefined;
  isLoading: boolean;
}

const TransferOutSection: React.FC<TransferOutSectionProps> = ({ data, isLoading }) => {
  const { t } = useLanguage();

  // Row-level status based on item data and yield variance
  const getRowStatus = (item: DailyTransferOutItem): string => {
    if (item.category === 'rejected_material') return 'rejected';
    if (item.category === 'remaining_material') return 'remainder';
    if (item.category === 'by_product') return 'by-product';
    if (item.category === 'by_product_marketable') return 'by-product-marketable';
    if (item.category === 'by_product_production') return 'by-product-production';
    if (isYieldVarianceWarning(item, undefined)) return 'variance-warning';
    if (item.actual_quantity != null || item.actual_weight != null) return 'completed';
    return 'estimate';
  };

  // Status badge rendering
  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      estimate: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-800 dark:text-amber-300', label: t('dailyTransferOut.estimate') },
      completed: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-800 dark:text-green-300', label: t('dailyTransferOut.completed') },
      rejected: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-800 dark:text-red-300', label: t('dailyTransferOut.rejected') },
      remainder: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-800 dark:text-blue-300', label: t('dailyTransferOut.remainder') },
      'by-product': { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-800 dark:text-purple-300', label: t('dailyTransferOut.byProduct') },
      'by-product-marketable': { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-800 dark:text-orange-300', label: t('dailyTransferOut.byProductMarketable') },
      'by-product-production': { bg: 'bg-teal-100 dark:bg-teal-900/40', text: 'text-teal-800 dark:text-teal-300', label: t('dailyTransferOut.byProductProduction') },
      'variance-warning': { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-800 dark:text-yellow-300', label: t('dailyTransferOut.varianceWarning') },
    };
    const badge = badges[status];
    if (!badge) return null;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  // Render a category table (finished products show expected columns, others don't)
  const renderCategoryTable = (
    title: string,
    icon: React.ReactNode,
    items: DailyTransferOutItem[],
    showExpected: boolean
  ) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="px-5 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
            {title}
          </h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ({items.length})
          </span>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="px-5 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
          No items
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-max min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/30">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                  {t('dailyTransferOut.itemNumber')}
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                  {t('dailyTransferOut.itemName')}
                </th>
                {showExpected && (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                    {t('dailyTransferOut.packing')}
                  </th>
                )}
                {showExpected && (
                  <>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                      {t('dailyTransferOut.expectedQty')}
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                      {t('dailyTransferOut.expectedWeight')}
                    </th>
                  </>
                )}
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                  {t('dailyTransferOut.actualQty')}
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                  {t('dailyTransferOut.actualWeight')}
                </th>
                {showExpected && (
                  <>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                      {t('dailyTransferOut.expectedYF')}
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                      {t('dailyTransferOut.actualYF')}
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                      {t('dailyTransferOut.variance')}
                    </th>
                  </>
                )}
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                  {t('dailyTransferOut.status')}
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                  {t('dailyTransferOut.notes')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {items.map((item) => {
                const rowStatus = getRowStatus(item);
                const variancePct = showExpected ? getItemYieldVariance(item) : null;
                const actualYF = showExpected ? getItemActualYF(item) : null;

                return (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                      rowStatus === 'variance-warning'
                        ? 'bg-yellow-50/50 dark:bg-yellow-900/10'
                        : ''
                    }`}
                  >
                    <td className="px-4 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {item.item_number}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {item.item_chinese_name && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{item.item_chinese_name}</div>
                      )}
                      <div>{item.item_name || '-'}</div>
                    </td>
                    {showExpected && (
                      <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {item.packing || '-'}
                      </td>
                    )}
                    {showExpected && (
                      <>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {item.expected_quantity != null
                            ? Number(item.expected_quantity).toLocaleString()
                            : '-'}
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {item.expected_weight != null
                            ? Number(item.expected_weight).toFixed(2)
                            : '-'}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-2 text-sm text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {item.actual_quantity != null
                        ? Number(item.actual_quantity).toLocaleString()
                        : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {item.actual_weight != null
                        ? Number(item.actual_weight).toFixed(2)
                        : '-'}
                    </td>
                    {showExpected && (
                      <>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {item.yield_factor != null && Number(item.yield_factor) > 0
                            ? formatYield(Number(item.yield_factor))
                            : '?'}
                        </td>
                        <td className={`px-4 py-2 text-sm text-right font-medium whitespace-nowrap ${getActualYFColor(item)}`}>
                          {actualYF != null ? formatYield(actualYF) : '-'}
                        </td>
                        <td className={`px-4 py-2 text-sm text-right font-medium whitespace-nowrap ${getYieldVarianceColor(variancePct, undefined)}`}>
                          {formatYieldVariancePct(variancePct)}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-2 whitespace-nowrap">{getStatusBadge(rowStatus)}</td>
                    <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {item.notes || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Truck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('endOfDaySummary.transferOutSummary')}
          </h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // No data state
  if (!data) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Truck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('endOfDaySummary.transferOutSummary')}
          </h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          {t('endOfDaySummary.noTransferData')}
        </p>
      </div>
    );
  }

  const dto = data;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 space-y-5">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <Truck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('endOfDaySummary.transferOutSummary')}
        </h2>
      </div>

      {/* 5 Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Available Remaining Raw Material — LIVE net on-hand (drives ship-back) */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Archive className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('dailyTransferOut.remainingMaterials')}
            </span>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Items:</span>
              <span className="font-medium text-gray-900 dark:text-white">{dto.totals.remaining.count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Weight:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {dto.totals.remaining.total_weight.toFixed(2)} lbs
              </span>
            </div>
          </div>
        </div>

        {/* Today's Remainder — WCO-2606: GROSS remainder produced this day (frozen), reconciles with
            Yield Variance. Independent of the on-hand figure above (which nets out reuse/ship-back). */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Archive className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('dailyTransferOut.remainderProducedToday')}
            </span>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Items:</span>
              <span className="font-medium text-gray-900 dark:text-white">{dto.totals.remainder_produced_today?.count ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Weight:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {(dto.totals.remainder_produced_today?.total_weight ?? 0).toFixed(2)} lbs
              </span>
            </div>
          </div>
        </div>

        {/* Finished Products */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('dailyTransferOut.finishedProducts')}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div>
              <span className="text-gray-500">{t('endOfDaySummary.expQty')}:</span>{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {dto.totals.finished.expected_quantity.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-gray-500">{t('endOfDaySummary.actQty')}:</span>{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {dto.totals.finished.actual_quantity.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-gray-500">{t('endOfDaySummary.expWeight')}:</span>{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {dto.totals.finished.expected_weight.toFixed(2)} lbs
              </span>
            </div>
            <div>
              <span className="text-gray-500">{t('endOfDaySummary.actWeight')}:</span>{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {dto.totals.finished.actual_weight.toFixed(2)} lbs
              </span>
            </div>
          </div>
        </div>

        {/* Rejected Materials */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('dailyTransferOut.rejectedMaterials')}
            </span>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Items:</span>
              <span className="font-medium text-gray-900 dark:text-white">{dto.totals.rejected.count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Weight:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {dto.totals.rejected.total_weight.toFixed(2)} lbs
              </span>
            </div>
          </div>
        </div>

        {/* By-Products */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Boxes className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('dailyTransferOut.byProducts')}
            </span>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Items:</span>
              <span className="font-medium text-gray-900 dark:text-white">{dto.totals.by_products?.count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Weight:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {(dto.totals.by_products?.total_weight || 0).toFixed(2)} lbs
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 4 Category Tables */}
      <div className="space-y-4">
        {renderCategoryTable(
          t('dailyTransferOut.finishedProducts'),
          <Package className="w-4 h-4 text-green-600" />,
          dto.finished_products || [],
          true
        )}
        {renderCategoryTable(
          t('dailyTransferOut.rejectedMaterials'),
          <AlertTriangle className="w-4 h-4 text-red-600" />,
          dto.rejected_materials || [],
          false
        )}
        {renderCategoryTable(
          t('dailyTransferOut.remainingMaterials'),
          <Archive className="w-4 h-4 text-blue-600" />,
          dto.remaining_materials || [],
          false
        )}
        {renderCategoryTable(
          t('dailyTransferOut.byProducts'),
          <Boxes className="w-4 h-4 text-purple-600" />,
          dto.by_products || [],
          false
        )}
      </div>
    </div>
  );
};

export default TransferOutSection;
